import React from 'react';
import { X, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Human-readable labels for the frozen feature keys (features.config.json).
const FEATURE_LABELS = {
  api_access: 'API Access',
  webhooks: 'Webhooks',
  white_label: 'White-label Branding',
  team_members: 'Team Members',
  csv_pdf_export: 'CSV & PDF Export',
  custom_tokens: 'Custom Tokens',
  auto_convert: 'Auto-convert',
  recurring_payments: 'Recurring Payments',
  advanced_analytics: 'Advanced Analytics',
  custom_checkout: 'Custom Checkout',
  daily_summary_email: 'Daily Summary Email',
  weekly_report_email: 'Weekly Report Email',
  priority_dispute: 'Priority Dispute Support',
};

const FREE_FEATURES = [
  'API keys & webhooks',
  'Accept crypto on 5 chains',
  'Hosted checkout',
  '2.0% transaction fee',
];

const PREMIUM_FEATURES = [
  '12+ chains & 50+ tokens',
  'Advanced analytics & reports',
  'Custom-branded & white-label checkout',
  'Auto-convert & recurring payments',
  'Priority support · 10x API rate limits',
  '1.0% transaction fee (save 50%)',
];

// Plan-choice modal (NOT a paywall). Shown when a merchant taps a Premium
// feature. Presents Free and Premium side by side and lets them CHOOSE —
// "Continue with Free" keeps them on the free plan; "Upgrade to Premium" routes
// to billing. Props kept stable (feature/onClose/onUpgrade) for all call sites.
const UpsellModal = ({ feature, onClose, onUpgrade }) => {
  const navigate = useNavigate();
  const label = FEATURE_LABELS[feature] || feature;

  const handleUpgrade = () => {
    if (onUpgrade) onUpgrade();
    else navigate('/dashboard/billing');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal plan-choice-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Choose your plan</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <p className="plan-choice-lead">
            {label ? <><strong>{label}</strong> is part of Premium. </> : null}
            You can keep using MaxelPay on Free — upgrade only if you want more.
          </p>

          <div className="plan-choice-grid">
            <div className="plan-choice-card">
              <div className="plan-choice-name">Free</div>
              <div className="plan-choice-price"><span className="amt">$0</span><span className="per">/mo</span></div>
              <ul className="plan-choice-feats">
                {FREE_FEATURES.map((f) => (
                  <li key={f}><Check size={15} /> {f}</li>
                ))}
              </ul>
              <button className="rd-btn rd-btn-secondary rd-btn-block" onClick={onClose}>
                Continue with Free
              </button>
            </div>

            <div className="plan-choice-card recommended">
              <span className="plan-choice-badge">Recommended</span>
              <div className="plan-choice-name">Premium</div>
              <div className="plan-choice-price"><span className="amt">$49</span><span className="per">/mo</span></div>
              <ul className="plan-choice-feats">
                {PREMIUM_FEATURES.map((f) => (
                  <li key={f}><Check size={15} /> {f}</li>
                ))}
              </ul>
              <button className="rd-btn rd-btn-purple rd-btn-block" onClick={handleUpgrade}>
                Upgrade to Premium
              </button>
            </div>
          </div>

          <p className="plan-choice-foot">No credit card to stay on Free. Cancel Premium anytime.</p>
        </div>
      </div>

      <style>{`
        .plan-choice-modal { max-width: 620px; width: 100%; }
        .plan-choice-lead { font-size: 14px; color: var(--ink-secondary); margin-bottom: 18px; line-height: 1.5; }
        .plan-choice-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .plan-choice-card {
          position: relative; border: 1px solid var(--line-strong); border-radius: var(--r-lg);
          padding: 20px 18px; background: var(--bg-surface); display: flex; flex-direction: column;
        }
        .plan-choice-card.recommended { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
        .plan-choice-badge {
          position: absolute; top: -10px; left: 18px; background: var(--accent); color: #fff;
          font-size: 11px; font-weight: 700; padding: 2px 10px; border-radius: var(--r-pill);
        }
        .plan-choice-name { font-size: 13px; font-weight: 700; color: var(--ink); text-transform: uppercase; letter-spacing: 0.04em; }
        .plan-choice-price { display: flex; align-items: baseline; gap: 3px; margin: 6px 0 14px; }
        .plan-choice-price .amt { font-family: var(--font-mono); font-size: 30px; font-weight: 700; color: var(--ink); }
        .plan-choice-price .per { font-size: 14px; color: var(--ink-muted); }
        .plan-choice-feats { list-style: none; padding: 0; margin: 0 0 18px; display: flex; flex-direction: column; gap: 9px; flex: 1; }
        .plan-choice-feats li { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--ink-secondary); line-height: 1.4; }
        .plan-choice-feats li svg { color: var(--ok); flex-shrink: 0; margin-top: 1px; }
        .plan-choice-foot { text-align: center; font-size: 12px; color: var(--ink-muted); margin-top: 16px; }
        @media (max-width: 576px) {
          .plan-choice-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default UpsellModal;
